/**
 * CLIKKER FIREBASE
 * Shared Firebase, authentication, Firestore and Storage foundation for all Clikker pages.
 *
 * Compatible with Firebase Web SDK "compat"/namespaced builds:
 *   firebase-app-compat.js
 *   firebase-auth-compat.js
 *   firebase-firestore-compat.js
 *   firebase-storage-compat.js
 *
 * Load this file before page-specific scripts.
 *
 * Version: 2.5.2 — public storefront fast-path
 */

(function (window, document) {
  "use strict";

  if (window.Clikker?.__coreLoaded) {
    console.warn("[Clikker] clikker-firebase.js is already loaded.");
    return;
  }

  // ============================================================
  // CONFIGURATION
  // ============================================================

  const FIREBASE_CONFIG = Object.freeze({
    apiKey: "AIzaSyA4nalqu1P4rPJFOnHtGlhQM3r4tsmaU5w",
    authDomain: "creator-dashboard-7273e.firebaseapp.com",
    projectId: "creator-dashboard-7273e",
    storageBucket: "creator-dashboard-7273e.firebasestorage.app",
    messagingSenderId: "754577832395",
    appId: "1:754577832395:web:0761eb7ed1e27622d21e4e"
  });

  const CORE_CONFIG = Object.freeze({
    version: "2.5.2",

    routes: {
      login: "/pages/clikker-login?mode=login",
      signup: "/pages/clikker-login?mode=signup",
      forgot: "/pages/clikker-login?mode=forgot",
      profile: "/pages/clikker-profile",
      dashboard: "/pages/clikker-profile?panel=dashboard",
      onboarding: "/pages/clikker-profile?panel=onboarding",
      products: "/pages/clikker-profile?panel=products",
      orders: "/pages/clikker-profile?panel=orders",
      payouts: "/pages/clikker-profile?panel=payouts",
      store: "/pages/clikker-profile?panel=store",
      settings: "/pages/clikker-profile?panel=settings",
      publicStore: "/pages/c?creator=",
      admin: "/pages/clikker-admin",
      suspended: "/pages/clikker-login?mode=login&reason=suspended"
    },

    collections: {
      users: "users",
      creators: "creators",
      stores: "stores",
      products: "products",
      productTemplates: "productTemplates",
      orders: "orders",
      orderItems: "orderItems",
      customers: "customers",
      notifications: "notifications",
      activityLogs: "activityLogs"
    },

    defaults: {
      currency: "INR",
      locale: "en-IN",
      country: "IN",
      timezone: "Asia/Kolkata"
    },

    uploadLimits: {
      image: 10 * 1024 * 1024,
      product: 250 * 1024 * 1024,
      document: 25 * 1024 * 1024,
      kyc: 10 * 1024 * 1024
    }
  });

  // ============================================================
  // INTERNAL STATE
  // ============================================================

  let resolveReady;
  let rejectReady;
  let readySettled = false;

  const readyPromise = new Promise((resolve, reject) => {
    resolveReady = resolve;
    rejectReady = reject;
  });

  function settleReady(value, error) {
    if (readySettled) return;
    readySettled = true;
    if (error) rejectReady(error);
    else resolveReady(value);
  }

  const state = {
    initialized: false,
    ready: false,
    authResolved: false,
    firebaseUser: null,
    user: null,
    creator: null,
    store: null,
    page: null,
    authUnsubscribe: null
  };

  const services = {
    app: null,
    auth: null,
    db: null,
    storage: null
  };

  // ============================================================
  // SMALL UTILITIES
  // ============================================================

  function emit(name, detail = {}) {
    window.dispatchEvent(
      new CustomEvent(`clikker:${name}`, {
        detail: {
          ...detail,
          timestamp: new Date().toISOString()
        }
      })
    );
  }

  function safeConsole(method, ...args) {
    try {
      (console[method] || console.log)(...args);
    } catch (_) {
      // no-op
    }
  }

  function clone(value) {
    if (value == null) return value;
    try {
      return JSON.parse(JSON.stringify(value));
    } catch (_) {
      return value;
    }
  }

  function cleanString(value, fallback = "") {
    if (value == null) return fallback;
    return String(value).trim();
  }

  function normalizeEmail(value) {
    return cleanString(value).toLowerCase();
  }

  function compactObject(input) {
    if (!input || typeof input !== "object") return {};

    return Object.entries(input).reduce((out, [key, value]) => {
      if (value !== undefined) out[key] = value;
      return out;
    }, {});
  }

  function toArray(value) {
    if (Array.isArray(value)) return value;
    if (value == null || value === "") return [];
    return [value];
  }

  function unique(items) {
    return [...new Set(toArray(items).filter(Boolean))];
  }

  function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  function normalizeRole(role) {
    return cleanString(role, "creator").toLowerCase().replace(/\s+/g, "_");
  }

  function normalizeStatus(status, fallback = "active") {
    return cleanString(status, fallback).toLowerCase().replace(/\s+/g, "_");
  }

  function isOnline() {
    return navigator.onLine !== false;
  }

  function getStateSnapshot() {
    return {
      initialized: state.initialized,
      ready: state.ready,
      authResolved: state.authResolved,
      firebaseUser: state.firebaseUser,
      user: clone(state.user),
      creator: clone(state.creator),
      store: clone(state.store),
      page: clone(state.page)
    };
  }

  function escapeHTML(value) {
    const div = document.createElement("div");
    div.textContent = value == null ? "" : String(value);
    return div.innerHTML;
  }

  function safeURL(value, options = {}) {
    const {
      allowRelative = false,
      allowedProtocols = ["https:", "http:"],
      assumeHttps = true
    } = options;

    let raw = cleanString(value);
    if (!raw) return "";

    try {
      // Allow only same-origin path-relative URLs. Protocol-relative values
      // such as //example.com must never bypass protocol validation.
      if (allowRelative && /^\/(?!\/)/.test(raw)) return raw;

      if (
        assumeHttps &&
        !/^[a-z][a-z0-9+.-]*:/i.test(raw) &&
        !raw.startsWith("//")
      ) {
        raw = `https://${raw}`;
      }

      const parsed = new URL(raw, window.location.origin);
      return allowedProtocols.includes(parsed.protocol) ? parsed.href : "";
    } catch (_) {
      return "";
    }
  }

  function slugify(value) {
    return cleanString(value)
      .toLowerCase()
      .normalize("NFKD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .replace(/-{2,}/g, "")
      .slice(0, 60);
  }

  function randomId(prefix = "") {
    const body =
      window.crypto?.randomUUID?.() ||
      `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;

    return prefix ? `${prefix}_${body}` : body;
  }

  function parseDate(value) {
    if (!value) return null;

    if (typeof value.toDate === "function") {
      const date = value.toDate();
      return Number.isNaN(date.getTime()) ? null : date;
    }

    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value;
    }

    if (typeof value === "object" && Number.isFinite(value.seconds)) {
      const date = new Date(value.seconds * 1000);
      return Number.isNaN(date.getTime()) ? null : date;
    }

    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  function formatDate(value, options = {}) {
    const date = parseDate(value);
    if (!date) return "—";

    return new Intl.DateTimeFormat(
      options.locale || CORE_CONFIG.defaults.locale,
      options.format || {
        day: "2-digit",
        month: "short",
        year: "numeric"
      }
    ).format(date);
  }

  function formatDateTime(value, options = {}) {
    const date = parseDate(value);
    if (!date) return "—";

    return new Intl.DateTimeFormat(
      options.locale || CORE_CONFIG.defaults.locale,
      options.format || {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      }
    ).format(date);
  }

  function formatCurrency(value, options = {}) {
    const amount = Number(value || 0);
    return new Intl.NumberFormat(
      options.locale || CORE_CONFIG.defaults.locale,
      {
        style: "currency",
        currency: options.currency || CORE_CONFIG.defaults.currency,
        maximumFractionDigits:
          options.maximumFractionDigits !== undefined
            ? options.maximumFractionDigits
            : 2
      }
    ).format(Number.isFinite(amount) ? amount : 0);
  }

  function formatMoneyFromPaise(value, options = {}) {
    return formatCurrency(Number(value || 0) / 100, options);
  }

  function getPageConfig() {
    const root =
      document.querySelector("[data-clikker-page]") ||
      document.getElementById("clikker-app") ||
      document.body;

    const dataset = root?.dataset || {};

    return {
      root,
      name: cleanString(dataset.clikkerPage || dataset.page || document.body?.dataset?.template),
      requireAuth:
        dataset.requireAuth === "true" ||
        dataset.auth === "required" ||
        document.body?.dataset?.requireAuth === "true",
      guestOnly:
        dataset.guestOnly === "true" ||
        dataset.auth === "guest" ||
        document.body?.dataset?.guestOnly === "true",
      allowedRoles: cleanString(
        dataset.allowedRoles || document.body?.dataset?.allowedRoles
      )
        .split(",")
        .map((role) => normalizeRole(role))
        .filter(Boolean),
      requireStore:
        dataset.requireStore === "true" ||
        document.body?.dataset?.requireStore === "true",
      redirect:
        cleanString(dataset.redirect || document.body?.dataset?.redirect)
    };
  }

  function redirect(url, options = {}) {
    const target = safeURL(url, {
      allowRelative: true,
      allowedProtocols: ["https:", "http:"]
    });

    if (!target) return false;

    try {
      const parsed = new URL(target, window.location.origin);
      if (options.allowExternal !== true && parsed.origin !== window.location.origin) {
        safeConsole("warn", "[Clikker] Blocked external redirect:", parsed.href);
        return false;
      }
    } catch (_) {
      return false;
    }

    if (options.replace !== false) window.location.replace(target);
    else window.location.assign(target);

    return true;
  }

  function addQueryParams(url, params = {}) {
    const raw = cleanString(url);
    if (!raw) return "";

    try {
      const parsed = new URL(raw, window.location.origin);

      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          parsed.searchParams.set(key, String(value));
        }
      });

      if (parsed.origin === window.location.origin) {
        return `${parsed.pathname}${parsed.search}${parsed.hash}`;
      }

      return parsed.href;
    } catch (_) {
      return raw;
    }
  }

  // ============================================================
  // UI HELPERS
  // ============================================================

  function ensureToastRoot() {
    let root = document.getElementById("clikker-toast-root");

    if (!root) {
      root = document.createElement("div");
      root.id = "clikker-toast-root";
      root.setAttribute("aria-live", "polite");
      root.setAttribute("aria-atomic", "true");
      root.style.cssText = [
        "position:fixed",
        "right:18px",
        "bottom:18px",
        "z-index:2147483647",
        "display:flex",
        "flex-direction:column",
        "gap:10px",
        "width:min(390px,calc(100vw - 36px))",
        "pointer-events:none"
      ].join(";");
      document.body.appendChild(root);
    }

    return root;
  }

  function showToast(message, type = "info", options = {}) {
    const text = cleanString(message);
    if (!text) return null;

    const root = ensureToastRoot();
    const toast = document.createElement("div");
    const duration = Number(options.duration || 4000);

    const palette = {
      success: { bg: "#ecfdf3", border: "#a7f3d0", text: "#065f46", icon: "✓" },
      error: { bg: "#fef2f2", border: "#fecaca", text: "#991b1b", icon: "!" },
      warning: { bg: "#fffbeb", border: "#fde68a", text: "#92400e", icon: "!" },
      info: { bg: "#eff6ff", border: "#bfdbfe", text: "#1e40af", icon: "i" }
    };

    const theme = palette[type] || palette.info;

    toast.setAttribute("role", type === "error" ? "alert" : "status");
    toast.style.cssText = [
      "pointer-events:auto",
      "display:flex",
      "align-items:flex-start",
      "gap:10px",
      "padding:13px 14px",
      "border-radius:12px",
      `background:${theme.bg}`,
      `border:1px solid ${theme.border}`,
      `color:${theme.text}`,
      "box-shadow:0 12px 30px rgba(15,23,42,.14)",
      "font:500 14px/1.45 system-ui,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",
      "opacity:0",
      "transform:translateY(8px)",
      "transition:opacity .18s ease,transform .18s ease"
    ].join(";");

    const icon = document.createElement("span");
    icon.textContent = theme.icon;
    icon.style.cssText =
      "display:grid;place-items:center;flex:0 0 22px;width:22px;height:22px;border-radius:999px;border:1px solid currentColor;font-weight:800;";

    const body = document.createElement("div");
    body.style.cssText = "flex:1;min-width:0;white-space:pre-wrap;overflow-wrap:anywhere;";
    body.textContent = text;

    const close = document.createElement("button");
    close.type = "button";
    close.setAttribute("aria-label", "Close notification");
    close.textContent = "×";
    close.style.cssText =
      "border:0;background:transparent;color:inherit;font-size:20px;line-height:1;cursor:pointer;padding:0 0 0 8px;";

    toast.append(icon, body, close);
    root.appendChild(toast);

    const remove = () => {
      toast.style.opacity = "0";
      toast.style.transform = "translateY(8px)";
      window.setTimeout(() => toast.remove(), 180);
    };

    close.addEventListener("click", remove);

    requestAnimationFrame(() => {
      toast.style.opacity = "1";
      toast.style.transform = "translateY(0)";
    });

    if (duration > 0) window.setTimeout(remove, duration);

    return toast;
  }

  function setLoading(target, loading = true, options = {}) {
    const element =
      typeof target === "string" ? document.querySelector(target) : target;

    if (!element) return false;

    if (loading) {
      if (!element.dataset.clikkerOriginalHtml) {
        element.dataset.clikkerOriginalHtml = element.innerHTML;
      }

      element.disabled = true;
      element.setAttribute("aria-busy", "true");

      if (options.text) {
        element.textContent = options.text;
      } else if (element.tagName === "BUTTON") {
        element.textContent = "Please wait…";
      }

      element.classList.add("is-loading");
    } else {
      element.disabled = false;
      element.removeAttribute("aria-busy");
      element.classList.remove("is-loading");

      if (options.restore !== false && element.dataset.clikkerOriginalHtml) {
        element.innerHTML = element.dataset.clikkerOriginalHtml;
        delete element.dataset.clikkerOriginalHtml;
      }
    }

    return true;
  }

  async function confirmAction(message, options = {}) {
    return window.confirm(cleanString(message, "Are you sure?"));
  }

  function copyToClipboard(value) {
    const text = cleanString(value);
    if (!text) return Promise.reject(new Error("Nothing to copy."));

    if (navigator.clipboard?.writeText) {
      return navigator.clipboard.writeText(text);
    }

    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.select();

    const ok = document.execCommand("copy");
    textarea.remove();

    return ok
      ? Promise.resolve()
      : Promise.reject(new Error("Copy failed."));
  }

  // ============================================================
  // FIREBASE INITIALIZATION
  // ============================================================

  function assertFirebaseAvailable() {
    if (!window.firebase) {
      throw new Error(
        "Firebase SDK is missing. Load Firebase compat scripts before clikker-firebase.js."
      );
    }

    if (typeof window.firebase.initializeApp !== "function") {
      throw new Error("Firebase App SDK is not available.");
    }

    if (typeof window.firebase.firestore !== "function") {
      throw new Error("Firebase Firestore SDK is not available.");
    }
  }

  function initializeFirebase() {
    assertFirebaseAvailable();

    const existingApp = (window.firebase.apps || []).find((app) =>
      app?.options?.projectId === FIREBASE_CONFIG.projectId
    );

    services.app = existingApp || window.firebase.initializeApp(FIREBASE_CONFIG);
    services.db = services.app.firestore();

    if (typeof window.firebase.auth === "function") {
      services.auth = services.app.auth();

      // Explicit local persistence keeps creator sessions stable across refreshes.
      if (window.firebase.auth.Auth?.Persistence?.LOCAL) {
        services.auth
          .setPersistence(window.firebase.auth.Auth.Persistence.LOCAL)
          .catch((error) =>
            safeConsole("warn", "[Clikker] Auth persistence could not be set:", error)
          );
      }
    }

    if (typeof window.firebase.storage === "function") {
      services.storage = services.app.storage();
    }

    state.initialized = true;
    emit("firebase-ready", {
      projectId: FIREBASE_CONFIG.projectId,
      version: CORE_CONFIG.version
    });
  }

  function serverTimestamp() {
    return window.firebase.firestore.FieldValue.serverTimestamp();
  }

  function increment(value = 1) {
    return window.firebase.firestore.FieldValue.increment(Number(value || 0));
  }

  function arrayUnion(...values) {
    return window.firebase.firestore.FieldValue.arrayUnion(...values);
  }

  function arrayRemove(...values) {
    return window.firebase.firestore.FieldValue.arrayRemove(...values);
  }

  function deleteField() {
    return window.firebase.firestore.FieldValue.delete();
  }

  function timestampFromDate(value) {
    const date = parseDate(value);
    if (!date) throw new Error("Invalid date.");
    return window.firebase.firestore.Timestamp.fromDate(date);
  }

  // ============================================================
  // FIRESTORE HELPERS
  // ============================================================

  function collectionRef(name) {
    const collectionName = cleanString(name);
    if (!collectionName) throw new Error("Collection name is required.");
    return services.db.collection(collectionName);
  }

  function documentRef(collectionName, documentId) {
    const id = cleanString(documentId);
    if (!id) throw new Error("Document ID is required.");
    return collectionRef(collectionName).doc(id);
  }

  async function getDocument(collectionName, documentId, options = {}) {
    const snapshot = await documentRef(collectionName, documentId).get(
      options.source ? { source: options.source } : undefined
    );

    if (!snapshot.exists) return null;

    return {
      id: snapshot.id,
      ...snapshot.data()
    };
  }

  async function createDocument(collectionName, data, options = {}) {
    const payload = compactObject({
      ...data,
      created_at: data?.created_at || serverTimestamp(),
      updated_at: serverTimestamp()
    });

    if (options.id) {
      const ref = documentRef(collectionName, options.id);
      await ref.set(payload, { merge: options.merge === true });
      return options.id;
    }

    const ref = await collectionRef(collectionName).add(payload);
    return ref.id;
  }

  async function setDocument(collectionName, documentId, data, options = {}) {
    const payload = compactObject({
      ...data,
      updated_at: serverTimestamp()
    });

    await documentRef(collectionName, documentId).set(payload, {
      merge: options.merge !== false
    });

    return documentId;
  }

  async function updateDocument(collectionName, documentId, data) {
    await documentRef(collectionName, documentId).update(
      compactObject({
        ...data,
        updated_at: serverTimestamp()
      })
    );

    return documentId;
  }

  async function deleteDocument(collectionName, documentId) {
    await documentRef(collectionName, documentId).delete();
    return true;
  }

  function applyQueryFilters(ref, options = {}) {
    let query = ref;

    for (const filter of options.filters || []) {
      if (!Array.isArray(filter) || filter.length < 3) continue;
      query = query.where(filter[0], filter[1], filter[2]);
    }

    for (const order of options.orderBy || []) {
      if (Array.isArray(order)) query = query.orderBy(order[0], order[1] || "asc");
      else if (order) query = query.orderBy(order, "asc");
    }

    if (options.startAfter) query = query.startAfter(options.startAfter);
    if (options.startAt) query = query.startAt(options.startAt);
    if (options.endBefore) query = query.endBefore(options.endBefore);
    if (options.endAt) query = query.endAt(options.endAt);

    if (Number.isFinite(options.limit) && options.limit > 0) {
      query = query.limit(options.limit);
    }

    return query;
  }

  async function queryDocuments(collectionName, options = {}) {
    const query = applyQueryFilters(collectionRef(collectionName), options);
    const snapshot = await query.get();

    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      __snapshot: options.includeSnapshot ? doc : undefined
    }));
  }

  function listenToDocument(collectionName, documentId, callback, errorCallback) {
    return documentRef(collectionName, documentId).onSnapshot(
      (snapshot) => {
        callback(
          snapshot.exists
            ? {
                id: snapshot.id,
                ...snapshot.data()
              }
            : null,
          snapshot
        );
      },
      errorCallback || ((error) => handleError(error, "listenToDocument"))
    );
  }

  function listenToQuery(collectionName, options, callback, errorCallback) {
    const query = applyQueryFilters(collectionRef(collectionName), options || {});

    return query.onSnapshot(
      (snapshot) => {
        callback(
          snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data()
          })),
          snapshot
        );
      },
      errorCallback || ((error) => handleError(error, "listenToQuery"))
    );
  }

  async function runTransaction(handler) {
    return services.db.runTransaction(async (transaction) => {
      return handler(transaction, {
        documentRef,
        serverTimestamp,
        increment,
        arrayUnion,
        arrayRemove,
        deleteField
      });
    });
  }

  async function runBatch(operations = []) {
    const batch = services.db.batch();

    for (const operation of operations) {
      const ref = documentRef(operation.collection, operation.id);

      if (operation.type === "set") {
        batch.set(ref, operation.data || {}, { merge: operation.merge !== false });
      } else if (operation.type === "update") {
        batch.update(ref, operation.data || {});
      } else if (operation.type === "delete") {
        batch.delete(ref);
      }
    }

    await batch.commit();
    return true;
  }

  // ============================================================
  // STORAGE HELPERS
  // ============================================================

  const MIME_GROUPS = Object.freeze({
    image: [
      "image/jpeg",
      "image/png",
      "image/webp",
      "image/gif",
      "image/svg+xml"
    ],
    document: [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.ms-powerpoint",
      "application/vnd.openxmlformats-officedocument.presentationml.presentation"
    ],
    product: [
      "application/pdf",
      "application/zip",
      "application/x-zip-compressed",
      "audio/mpeg",
      "audio/wav",
      "video/mp4",
      "video/webm"
    ]
  });

  function sanitizeFileName(name) {
    const raw = cleanString(name, "file");
    const dot = raw.lastIndexOf(".");
    const extension = dot >= 0 ? raw.slice(dot).toLowerCase() : "";
    const base = dot >= 0 ? raw.slice(0, dot) : raw;

    const safeBase =
      slugify(base).replace(/-/g, "_").slice(0, 80) || `file_${Date.now()}`;

    return `${safeBase}${extension}`;
  }

  function validateFile(file, options = {}) {
    if (!(file instanceof File) && !(file instanceof Blob)) {
      throw new TypeError("A valid file is required.");
    }

    const category = options.category || "product";
    const maxBytes =
      Number(options.maxBytes) ||
      CORE_CONFIG.uploadLimits[category] ||
      CORE_CONFIG.uploadLimits.product;

    if (file.size > maxBytes) {
      throw new Error(
        `File is too large. Maximum allowed size is ${Math.round(
          maxBytes / 1024 / 1024
        )} MB.`
      );
    }

    const allowedTypes =
      options.allowedTypes ||
      (category === "kyc"
        ? [...MIME_GROUPS.image, "application/pdf"]
        : MIME_GROUPS[category]);

    if (
      Array.isArray(allowedTypes) &&
      allowedTypes.length &&
      !allowedTypes.includes(file.type)
    ) {
      throw new Error(`Unsupported file type: ${file.type || "unknown"}.`);
    }

    return true;
  }

  function uploadFile(options = {}) {
    if (!services.storage) {
      return Promise.reject(
        new Error("Firebase Storage SDK is not loaded.")
      );
    }

    const {
      file,
      path,
      category = "product",
      fileName,
      metadata = {},
      onProgress
    } = options;

    validateFile(file, {
      category,
      maxBytes: options.maxBytes,
      allowedTypes: options.allowedTypes
    });

    const safePath = cleanString(path).replace(/^\/+|\/+$/g, "");
    if (!safePath) {
      return Promise.reject(new Error("Upload path is required."));
    }

    const finalName = sanitizeFileName(fileName || file.name || randomId("file"));
    const fullPath = `${safePath}/${Date.now()}_${finalName}`;
    const ref = services.storage.ref().child(fullPath);

    const uploadMetadata = {
      contentType: file.type || metadata.contentType || "application/octet-stream",
      customMetadata: {
        uploadedBy: state.firebaseUser?.uid || "public",
        originalName: cleanString(file.name || finalName),
        ...metadata.customMetadata
      },
      ...metadata
    };

    const task = ref.put(file, uploadMetadata);

    const promise = new Promise((resolve, reject) => {
      task.on(
        "state_changed",
        (snapshot) => {
          const progress =
            snapshot.totalBytes > 0
              ? Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)
              : 0;

          if (typeof onProgress === "function") {
            onProgress(progress, snapshot);
          }

          emit("upload-progress", {
            fullPath,
            progress,
            bytesTransferred: snapshot.bytesTransferred,
            totalBytes: snapshot.totalBytes
          });
        },
        (error) => {
          handleError(error, "uploadFile", false);
          reject(error);
        },
        async () => {
          try {
            const downloadURL = await task.snapshot.ref.getDownloadURL();

            const result = {
              downloadURL,
              fullPath: task.snapshot.ref.fullPath,
              fileName: finalName,
              originalName: file.name || finalName,
              fileSize: file.size,
              contentType: file.type || "",
              uploadedAt: new Date().toISOString()
            };

            emit("upload-complete", result);
            resolve(result);
          } catch (error) {
            reject(error);
          }
        }
      );
    });

    promise.cancel = () => task.cancel();
    promise.pause = () => task.pause();
    promise.resume = () => task.resume();
    promise.task = task;

    return promise;
  }

  async function deleteFile(fullPath) {
    if (!services.storage) {
      throw new Error("Firebase Storage SDK is not loaded.");
    }

    const path = cleanString(fullPath);
    if (!path) throw new Error("Storage path is required.");

    await services.storage.ref().child(path).delete();
    return true;
  }

  // ============================================================
  // NORMALIZERS FOR LEGACY + NEW DATA
  // ============================================================

  function normalizeUser(data = {}, id = "") {
    return {
      id: id || data.id || data.uid || "",
      uid: data.uid || id || "",
      email: cleanString(data.email),
      name: cleanString(data.name || data.display_name || data.displayName),
      photoURL: cleanString(
        data.photo_url ||
        data.photoURL ||
        data.google_photo_url ||
        data.googlePhotoURL ||
        data.profile_image ||
        data.profileImage
      ),
      role: normalizeRole(data.role || "creator"),
      status: normalizeStatus(data.status || "active"),
      creatorId: cleanString(data.creatorId || data.creator_id),
      storeId: cleanString(data.storeId || data.store_id),
      onboardingStatus: normalizeStatus(
        data.onboardingStatus || data.onboarding_status || "incomplete"
      ),
      raw: data
    };
  }

  function normalizeCreator(data = {}, id = "") {
    return {
      id: id || data.id || "",
      userId: cleanString(data.userId || data.user_id || data.uid),
      displayName: cleanString(
        data.displayName || data.display_name || data.name
      ),
      handle: cleanString(
        data.handle ||
          data.creatorHandle ||
          data.creator_handle ||
          data.creator_handles
      ),
      category: cleanString(data.category),
      audience: cleanString(data.audience),
      language: cleanString(data.language),
      bio: cleanString(data.bio || data.short_bio || data.shortBio),
      supportEmail: cleanString(
        data.supportEmail || data.support_email || data.email
      ),
      socialLinks: data.socialLinks || data.social_links || {},
      profileImage: cleanString(
        data.profileImage || data.profile_image || data.photoURL
      ),
      storeId: cleanString(data.storeId || data.store_id),
      kycStatus: normalizeStatus(
        data.kycStatus || data.kyc_status || "not_started"
      ),
      payoutStatus: normalizeStatus(
        data.payoutStatus || data.payout_status || "not_connected"
      ),
      onboardingStatus: normalizeStatus(
        data.onboardingStatus || data.onboarding_status || "incomplete"
      ),
      status: normalizeStatus(data.status || "active"),
      raw: data
    };
  }

  function normalizeStore(data = {}, id = "") {
    return {
      id: id || data.id || "",
      ownerId: cleanString(data.ownerId || data.owner_id),
      creatorId: cleanString(data.creatorId || data.creator_id),
      storeName: cleanString(
        data.storeName || data.store_name || data.name
      ),
      slug: cleanString(data.slug || data.handle),
      tagline: cleanString(data.tagline),
      shortBio: cleanString(data.shortBio || data.short_bio),
      about: cleanString(data.about),
      category: cleanString(data.category),
      audience: cleanString(data.audience),
      template: cleanString(data.template || "personal-brand"),
      status: normalizeStatus(data.status || "draft"),
      visibility: normalizeStatus(data.visibility || "private"),
      completionPercentage: Number(
        data.completionPercentage || data.completion_percentage || 0
      ),
      profileImage: cleanString(
        data.profileImage || data.profile_image
      ),
      bannerImage: cleanString(
        data.bannerImage || data.banner_image
      ),
      logo: cleanString(data.logo),
      primaryColor: cleanString(
        data.primaryColor || data.primary_color
      ),
      secondaryColor: cleanString(
        data.secondaryColor || data.secondary_color
      ),
      supportEmail: cleanString(
        data.supportEmail || data.support_email || data.contactEmail || data.contact_email
      ),
      language: cleanString(data.language),
      socialLinks: data.socialLinks || data.social_links || {},
      selectedProductCount: Number(
        data.selectedProductCount || data.selected_product_count || 0
      ),
      onboardingStatus: normalizeStatus(
        data.onboardingStatus || data.onboarding_status || "incomplete"
      ),
      raw: data
    };
  }

  function normalizeOrder(data = {}, id = "") {
    const amountPaiseRaw =
      data.amount_paise ?? data.amountPaise ?? data.total_paise;

    const amountRaw =
      data.amount ??
      data.total_price ??
      data.totalPrice ??
      data.price ??
      0;

    const amountPaise =
      amountPaiseRaw != null
        ? Number(amountPaiseRaw || 0)
        : Math.round(Number(amountRaw || 0) * 100);

    return {
      id: id || data.id || "",
      storeId: cleanString(data.storeId || data.store_id),
      creatorId: cleanString(data.creatorId || data.creator_id),
      creatorHandle: cleanString(
        data.creatorHandle ||
          data.creator_handle ||
          data.creator_handles
      ),
      customerId: cleanString(data.customerId || data.customer_id),
      customerName: cleanString(
        data.customerName || data.customer_name
      ),
      customerEmail: cleanString(
        data.customerEmail || data.customer_email
      ),
      phone: cleanString(data.phone || data.customer_phone),
      productId: cleanString(data.productId || data.product_id),
      productName: cleanString(
        data.productName || data.product_name || data.product
      ),
      amountPaise: Number.isFinite(amountPaise) ? amountPaise : 0,
      amount:
        Number.isFinite(amountPaise) ? amountPaise / 100 : Number(amountRaw || 0),
      currency: cleanString(
        data.currency || CORE_CONFIG.defaults.currency
      ).toUpperCase(),
      status: normalizeStatus(
        data.status || data.action_status || "pending"
      ),
      paymentStatus: normalizeStatus(
        data.paymentStatus ||
          data.payment_status ||
          data.financial_status ||
          "pending"
      ),
      fulfillmentStatus: normalizeStatus(
        data.fulfillmentStatus ||
          data.fulfillment_status ||
          data.action_status ||
          "pending"
      ),
      type: cleanString(
        data.type || data.drop_type || data.sub_drop_type || "general"
      ),
      createdAt: data.createdAt || data.created_at || null,
      updatedAt: data.updatedAt || data.updated_at || null,
      raw: data
    };
  }

  // ============================================================
  // USER, CREATOR AND STORE LOADING
  // ============================================================

  async function findFirst(collectionName, filters = []) {
    const rows = await queryDocuments(collectionName, {
      filters,
      limit: 1
    });

    return rows[0] || null;
  }

  function providerProfile(firebaseUser, providerId = "google.com") {
    const rows = Array.isArray(firebaseUser?.providerData)
      ? firebaseUser.providerData
      : [];

    return (
      rows.find((row) => cleanString(row?.providerId) === providerId) ||
      rows[0] ||
      null
    );
  }

  function resolveGoogleIdentity(result, firebaseUser) {
    const oauthProfile =
      result?.additionalUserInfo?.profile &&
      typeof result.additionalUserInfo.profile === "object"
        ? result.additionalUserInfo.profile
        : {};

    const provider = providerProfile(firebaseUser, "google.com") || {};

    const displayName = cleanString(
      firebaseUser?.displayName ||
      oauthProfile.name ||
      oauthProfile.given_name ||
      provider.displayName
    );

    const email = normalizeEmail(
      firebaseUser?.email ||
      oauthProfile.email ||
      provider.email
    );

    const photoURL = cleanString(
      firebaseUser?.photoURL ||
      oauthProfile.picture ||
      provider.photoURL
    );

    return {
      displayName,
      email,
      photoURL,
      oauthProfile,
      provider
    };
  }

  async function repairFirebaseGoogleProfile(firebaseUser, identity) {
    if (!firebaseUser || typeof firebaseUser.updateProfile !== "function") {
      return firebaseUser;
    }

    const updates = {};

    if (!cleanString(firebaseUser.displayName) && identity.displayName) {
      updates.displayName = identity.displayName;
    }

    if (!cleanString(firebaseUser.photoURL) && identity.photoURL) {
      updates.photoURL = identity.photoURL;
    }

    if (Object.keys(updates).length) {
      await firebaseUser.updateProfile(updates);

      /*
       * Reload so downstream pages receive the repaired Auth profile
       * immediately instead of waiting for the next browser session.
       */
      if (typeof firebaseUser.reload === "function") {
        await firebaseUser.reload();
      }
    }

    return firebaseUser;
  }

  async function ensureUserProfile(firebaseUser) {
    const usersCollection = CORE_CONFIG.collections.users;
    const existing = await getDocument(usersCollection, firebaseUser.uid);

    const authProvider = providerProfile(firebaseUser, "google.com") || {};
    const authEmail = normalizeEmail(
      firebaseUser.email || authProvider.email
    );
    const authDisplayName = cleanString(
      firebaseUser.displayName || authProvider.displayName
    );
    const authPhotoURL = cleanString(
      firebaseUser.photoURL || authProvider.photoURL
    );
    const providerIds = unique(
      (firebaseUser.providerData || [])
        .map((provider) => cleanString(provider?.providerId))
        .filter(Boolean)
    );

    /*
     * Google/Firebase account data is synchronized separately from the
     * creator's manually selected storefront image.
     *
     * Important:
     * - googlePhotoURL is safe to refresh on every authentication.
     * - photo_url is populated only when the user record does not already
     *   have an account/profile photo, so a custom image is never overwritten.
     * - creator/store profileImage fields are managed by clikker-profile.js.
     */
    if (existing) {
      const syncPayload = {
        uid: firebaseUser.uid,
        email: authEmail || normalizeEmail(existing.email),
        auth_display_name: authDisplayName,
        authDisplayName: authDisplayName,
        google_photo_url: authPhotoURL,
        googlePhotoURL: authPhotoURL,
        auth_provider_ids: providerIds,
        authProviderIds: providerIds,
        last_auth_sync_at: serverTimestamp(),
        lastAuthSyncAt: serverTimestamp(),
        updated_at: serverTimestamp()
      };

      if (!cleanString(existing.name) && authDisplayName) {
        syncPayload.name = authDisplayName;
      }

      if (
        authPhotoURL &&
        !cleanString(
          existing.photo_url ||
          existing.photoURL ||
          existing.profile_image ||
          existing.profileImage
        )
      ) {
        syncPayload.photo_url = authPhotoURL;
        syncPayload.photoURL = authPhotoURL;
      }

      await documentRef(usersCollection, firebaseUser.uid).set(syncPayload, {
        merge: true
      });

      return normalizeUser(
        {
          ...existing,
          ...syncPayload
        },
        firebaseUser.uid
      );
    }

    const nowPayload = {
      uid: firebaseUser.uid,
      email: authEmail,
      name: authDisplayName,
      auth_display_name: authDisplayName,
      authDisplayName: authDisplayName,
      photo_url: authPhotoURL,
      photoURL: authPhotoURL,
      google_photo_url: authPhotoURL,
      googlePhotoURL: authPhotoURL,
      auth_provider_ids: providerIds,
      authProviderIds: providerIds,
      role: "creator",
      status: "active",
      onboarding_status: "incomplete",
      onboardingStatus: "incomplete",
      creator_id: "",
      creatorId: "",
      store_id: "",
      storeId: "",
      created_at: serverTimestamp(),
      updated_at: serverTimestamp(),
      last_auth_sync_at: serverTimestamp(),
      lastAuthSyncAt: serverTimestamp()
    };

    await documentRef(usersCollection, firebaseUser.uid).set(nowPayload, {
      merge: true
    });

    return normalizeUser(nowPayload, firebaseUser.uid);
  }

  async function loadCreatorForUser(userProfile) {
    const creatorsCollection = CORE_CONFIG.collections.creators;

    if (userProfile.creatorId) {
      const direct = await getDocument(
        creatorsCollection,
        userProfile.creatorId
      );

      if (direct) return normalizeCreator(direct, userProfile.creatorId);
    }

    const byUserId = await findFirst(creatorsCollection, [
      ["user_id", "==", userProfile.uid]
    ]);

    if (byUserId) return normalizeCreator(byUserId, byUserId.id);

    const byCamelUserId = await findFirst(creatorsCollection, [
      ["userId", "==", userProfile.uid]
    ]);

    if (byCamelUserId) {
      return normalizeCreator(byCamelUserId, byCamelUserId.id);
    }

    return null;
  }

  async function loadStoreForContext(userProfile, creatorProfile) {
    const storesCollection = CORE_CONFIG.collections.stores;
    const storeId = userProfile.storeId || creatorProfile?.storeId || "";

    if (storeId) {
      const direct = await getDocument(storesCollection, storeId);
      if (direct) return normalizeStore(direct, storeId);
    }

    if (creatorProfile?.id) {
      const byCreatorSnake = await findFirst(storesCollection, [
        ["creator_id", "==", creatorProfile.id]
      ]);

      if (byCreatorSnake) {
        return normalizeStore(byCreatorSnake, byCreatorSnake.id);
      }

      const byCreatorCamel = await findFirst(storesCollection, [
        ["creatorId", "==", creatorProfile.id]
      ]);

      if (byCreatorCamel) {
        return normalizeStore(byCreatorCamel, byCreatorCamel.id);
      }
    }

    const byOwnerSnake = await findFirst(storesCollection, [
      ["owner_id", "==", userProfile.uid]
    ]);

    if (byOwnerSnake) {
      return normalizeStore(byOwnerSnake, byOwnerSnake.id);
    }

    const byOwnerCamel = await findFirst(storesCollection, [
      ["ownerId", "==", userProfile.uid]
    ]);

    if (byOwnerCamel) {
      return normalizeStore(byOwnerCamel, byOwnerCamel.id);
    }

    return null;
  }

  async function syncReferences(userProfile, creatorProfile, storeProfile) {
    const updates = {};

    if (creatorProfile?.id && userProfile.creatorId !== creatorProfile.id) {
      updates.creator_id = creatorProfile.id;
      updates.creatorId = creatorProfile.id;
    }

    if (storeProfile?.id && userProfile.storeId !== storeProfile.id) {
      updates.store_id = storeProfile.id;
      updates.storeId = storeProfile.id;
    }

    if (Object.keys(updates).length) {
      await updateDocument(
        CORE_CONFIG.collections.users,
        userProfile.uid,
        updates
      ).catch((error) => handleError(error, "syncReferences", false));
    }
  }

  async function loadAuthenticatedContext(firebaseUser) {
    const userProfile = await ensureUserProfile(firebaseUser);
    const creatorProfile = await loadCreatorForUser(userProfile);
    const storeProfile = await loadStoreForContext(
      userProfile,
      creatorProfile
    );

    state.firebaseUser = firebaseUser;
    state.user = userProfile;
    state.creator = creatorProfile;
    state.store = storeProfile;

    await syncReferences(userProfile, creatorProfile, storeProfile);

    emit("user-loaded", { user: clone(userProfile) });
    emit("creator-loaded", { creator: clone(creatorProfile) });
    emit("store-loaded", { store: clone(storeProfile) });

    return {
      firebaseUser,
      user: userProfile,
      creator: creatorProfile,
      store: storeProfile
    };
  }

  // ============================================================
  // AUTHENTICATION
  // ============================================================

  async function loginWithEmail(email, password) {
    if (!services.auth) {
      throw new Error("Firebase Auth SDK is not loaded.");
    }

    const normalizedEmail = normalizeEmail(email);
    const normalizedPassword = String(password || "");

    if (!normalizedEmail) throw new Error("Email address is required.");
    if (!normalizedPassword) throw new Error("Password is required.");

    const result = await services.auth.signInWithEmailAndPassword(
      normalizedEmail,
      normalizedPassword
    );

    return result.user;
  }

  async function persistSignupConsent(firebaseUser, options = {}) {
    if (!firebaseUser?.uid) return false;

    const termsAccepted = options.termsAccepted === true;
    const creatorPolicyAccepted = options.creatorPolicyAccepted === true;

    if (!termsAccepted && !creatorPolicyAccepted) return false;

    const payload = {
      consent_updated_at: serverTimestamp(),
      consentUpdatedAt: serverTimestamp()
    };

    if (termsAccepted) {
      payload.terms_accepted = true;
      payload.termsAccepted = true;
      payload.terms_accepted_at = serverTimestamp();
      payload.termsAcceptedAt = serverTimestamp();
      payload.terms_version = cleanString(options.termsVersion || "current");
      payload.termsVersion = cleanString(options.termsVersion || "current");
    }

    if (creatorPolicyAccepted) {
      payload.creator_policy_accepted = true;
      payload.creatorPolicyAccepted = true;
      payload.creator_policy_accepted_at = serverTimestamp();
      payload.creatorPolicyAcceptedAt = serverTimestamp();
      payload.creator_policy_version = cleanString(options.creatorPolicyVersion || "current");
      payload.creatorPolicyVersion = cleanString(options.creatorPolicyVersion || "current");
    }

    payload.consent_source = cleanString(options.consentSource || options.source || "signup");
    payload.consentSource = payload.consent_source;

    await setDocument(CORE_CONFIG.collections.users, firebaseUser.uid, payload, {
      merge: true
    });

    return true;
  }

  async function registerWithEmail(options = {}) {
    if (!services.auth) {
      throw new Error("Firebase Auth SDK is not loaded.");
    }

    const email = normalizeEmail(options.email);
    const password = String(options.password || "");
    const name = cleanString(options.name);

    if (!email) throw new Error("Email address is required.");
    if (password.length < 6) {
      throw new Error("Password must contain at least 6 characters.");
    }

    const result = await services.auth.createUserWithEmailAndPassword(
      email,
      password
    );

    if (name) {
      await result.user.updateProfile({ displayName: name });
    }

    try {
      await ensureUserProfile(result.user);
      await persistSignupConsent(result.user, options);

      if (options.sendEmailVerification === true && !result.user.emailVerified) {
        await result.user.sendEmailVerification();
      }
    } catch (profileError) {
      safeConsole(
        "warn",
        "[Clikker] Authentication succeeded but user profile creation/sync failed:",
        profileError
      );

      emit("profile-sync-failed", {
        uid: result.user.uid,
        code: profileError?.code || "",
        message: profileError?.message || ""
      });
    }

    return result.user;
  }

  async function loginWithGoogle(options = {}) {
    if (!services.auth) {
      throw new Error("Firebase Auth SDK is not loaded.");
    }

    const provider = new window.firebase.auth.GoogleAuthProvider();
    provider.addScope("profile");
    provider.addScope("email");
    provider.setCustomParameters({ prompt: "select_account" });

    const result = await services.auth.signInWithPopup(provider);
    let user = result.user;

    try {
      /*
       * Firebase normally maps Google's name/photo automatically, but older
       * linked accounts or incomplete Auth records can return blank fields.
       * Resolve identity from all reliable Google sources.
       */
      const identity = resolveGoogleIdentity(result, user);

      await repairFirebaseGoogleProfile(user, identity);

      /*
       * Re-acquire the current Auth user after updateProfile/reload so the
       * rest of Clikker sees the repaired displayName/photoURL immediately.
       */
      user = services.auth.currentUser || user;

      const finalIdentity = {
        displayName: cleanString(
          user?.displayName || identity.displayName
        ),
        email: normalizeEmail(
          user?.email || identity.email
        ),
        photoURL: cleanString(
          user?.photoURL || identity.photoURL
        )
      };

      /*
       * Save OAuth-derived values explicitly on the user document as account
       * metadata. These are fallbacks only; creator/store custom images remain
       * controlled by clikker-profile.js.
       */
      await documentRef(CORE_CONFIG.collections.users, user.uid).set(
        compactObject({
          uid: user.uid,
          email: finalIdentity.email,
          auth_display_name: finalIdentity.displayName,
          authDisplayName: finalIdentity.displayName,
          google_photo_url: finalIdentity.photoURL,
          googlePhotoURL: finalIdentity.photoURL,
          google_email: normalizeEmail(
            identity.oauthProfile?.email || finalIdentity.email
          ),
          googleEmail: normalizeEmail(
            identity.oauthProfile?.email || finalIdentity.email
          ),
          google_provider_id: cleanString(
            identity.provider?.providerId || "google.com"
          ),
          googleProviderId: cleanString(
            identity.provider?.providerId || "google.com"
          ),
          last_auth_sync_at: serverTimestamp(),
          lastAuthSyncAt: serverTimestamp(),
          updated_at: serverTimestamp()
        }),
        { merge: true }
      );

      await ensureUserProfile(user);

      const isNewUser = Boolean(result?.additionalUserInfo?.isNewUser);
      const mode = cleanString(
        options.mode || options.googleMode || "login"
      ).toLowerCase();

      if (isNewUser || mode === "signup") {
        await persistSignupConsent(user, options);
      }

      /*
       * Refresh in-memory state immediately. Without this, a redirect that
       * happens very quickly can expose stale state from before updateProfile.
       */
      state.firebaseUser = user;

      emit("google-auth-complete", {
        uid: user.uid,
        isNewUser,
        mode,
        displayName: finalIdentity.displayName,
        email: finalIdentity.email,
        photoURL: finalIdentity.photoURL,
        hasPhoto: Boolean(finalIdentity.photoURL)
      });
    } catch (profileError) {
      safeConsole(
        "warn",
        "[Clikker] Google authentication succeeded but profile sync failed:",
        profileError
      );

      emit("profile-sync-failed", {
        uid: user?.uid || "",
        code: profileError?.code || "",
        message: profileError?.message || ""
      });
    }

    return user;
  }

  async function sendPasswordReset(email) {
    if (!services.auth) {
      throw new Error("Firebase Auth SDK is not loaded.");
    }

    const normalizedEmail = normalizeEmail(email);
    if (!normalizedEmail) throw new Error("Email address is required.");
    await services.auth.sendPasswordResetEmail(normalizedEmail);
    return true;
  }

  async function logout(options = {}) {
    if (!services.auth) return true;

    await services.auth.signOut();

    state.firebaseUser = null;
    state.user = null;
    state.creator = null;
    state.store = null;

    emit("logout", {});

    if (options.redirect !== false) {
      redirect(options.redirectTo || CORE_CONFIG.routes.login);
    }

    return true;
  }

  function hasRole(roles) {
    const allowed = unique(toArray(roles).map(normalizeRole));
    if (!allowed.length) return true;
    return allowed.includes(normalizeRole(state.user?.role));
  }

  function isAuthenticated() {
    return Boolean(state.firebaseUser);
  }

  function isAccountActive() {
    return normalizeStatus(state.user?.status, "active") === "active";
  }

  function applyPageAccessRules() {
    const page = state.page || getPageConfig();

    if (page.guestOnly && state.firebaseUser) {
      redirect(page.redirect || CORE_CONFIG.routes.dashboard);
      return false;
    }

    if (page.requireAuth && !state.firebaseUser) {
      const returnUrl = `${window.location.pathname}${window.location.search}`;

      redirect(
        addQueryParams(page.redirect || CORE_CONFIG.routes.login, {
          return: returnUrl
        })
      );
      return false;
    }

    if (state.firebaseUser && !isAccountActive()) {
      redirect(CORE_CONFIG.routes.suspended);
      return false;
    }

    if (
      state.firebaseUser &&
      page.allowedRoles.length &&
      !hasRole(page.allowedRoles)
    ) {
      showToast("You do not have permission to access this page.", "error");
      redirect(CORE_CONFIG.routes.dashboard);
      return false;
    }

    if (state.firebaseUser && page.requireStore && !state.store) {
      redirect(CORE_CONFIG.routes.onboarding);
      return false;
    }

    return true;
  }

  function startAuthObserver() {
    if (typeof state.authUnsubscribe === "function") {
      state.authUnsubscribe();
      state.authUnsubscribe = null;
    }

    if (!services.auth) {
      state.authResolved = true;
      state.ready = true;

      const context = {
        firebaseUser: null,
        user: null,
        creator: null,
        store: null
      };

      settleReady(context);
      emit("ready", context);
      return;
    }

    state.authUnsubscribe = services.auth.onAuthStateChanged(
      async (firebaseUser) => {
        try {
          state.authResolved = true;

          let context = {
            firebaseUser: null,
            user: null,
            creator: null,
            store: null
          };

          if (firebaseUser) {
            state.firebaseUser = firebaseUser;

            /*
             * Public storefront fast-path
             * ----------------------------------------------------------
             * /pages/c only needs the Firebase Auth identity so the
             * storefront can determine whether ?preview=1 belongs to the
             * store owner. It does NOT need to read/sync users, creators
             * and stores before the public storefront can render.
             *
             * The previous behaviour ran loadAuthenticatedContext() here,
             * which can perform several sequential Firestore reads/writes
             * and made public creator stores wait for dashboard context.
             */
            if (state.page?.name === "public-store") {
              state.user = normalizeUser(
                {
                  uid: firebaseUser.uid,
                  email:
                    firebaseUser.email ||
                    providerProfile(firebaseUser, "google.com")?.email ||
                    "",
                  name:
                    firebaseUser.displayName ||
                    providerProfile(firebaseUser, "google.com")?.displayName ||
                    "",
                  photoURL:
                    firebaseUser.photoURL ||
                    providerProfile(firebaseUser, "google.com")?.photoURL ||
                    "",
                  role: "creator",
                  status: "active"
                },
                firebaseUser.uid
              );
              state.creator = null;
              state.store = null;

              context = {
                firebaseUser,
                user: state.user,
                creator: null,
                store: null,
                publicStoreFastPath: true
              };

              emit("public-store-auth-ready", {
                authenticated: true,
                uid: firebaseUser.uid
              });
            } else {
              try {
                context = await loadAuthenticatedContext(firebaseUser);
              } catch (contextError) {
                // Keep authentication usable even when Firestore rules or data
                // are not ready. Creator pages can show a separate data error.
                state.user = normalizeUser(
                  {
                    uid: firebaseUser.uid,
                    email:
                      firebaseUser.email ||
                      providerProfile(firebaseUser, "google.com")?.email ||
                      "",
                    name:
                      firebaseUser.displayName ||
                      providerProfile(firebaseUser, "google.com")?.displayName ||
                      "",
                    photoURL:
                      firebaseUser.photoURL ||
                      providerProfile(firebaseUser, "google.com")?.photoURL ||
                      "",
                    role: "creator",
                    status: "active",
                    onboarding_status: "incomplete"
                  },
                  firebaseUser.uid
                );
                state.creator = null;
                state.store = null;

                context = {
                  firebaseUser,
                  user: state.user,
                  creator: null,
                  store: null,
                  contextError
                };

                safeConsole(
                  "warn",
                  "[Clikker] Authenticated, but Firestore context could not load:",
                  contextError
                );

                emit("context-load-failed", {
                  code: contextError?.code || "",
                  message: contextError?.message || ""
                });
              }
            }
          } else {
            state.firebaseUser = null;
            state.user = null;
            state.creator = null;
            state.store = null;
            emit("auth-changed", { authenticated: false });
          }

          applyPageAccessRules();

          if (!state.ready) {
            state.ready = true;
            settleReady(context);
          }

          emit("auth-changed", {
            authenticated: Boolean(firebaseUser),
            firebaseUser
          });

          emit("ready", {
            ...context,
            page: clone(state.page)
          });
        } catch (error) {
          state.ready = false;
          handleError(error, "authObserver");
          settleReady(null, error);
        }
      },
      (error) => {
        state.authResolved = true;
        handleError(error, "authObserver");
        settleReady(null, error);
      }
    );
  }

  // ============================================================
  // STORE CREATION FOUNDATION
  // ============================================================

  async function isStoreSlugAvailable(slug, excludeStoreId = "") {
    const normalizedSlug = slugify(slug);
    if (!normalizedSlug) return false;

    const matches = await queryDocuments(CORE_CONFIG.collections.stores, {
      filters: [["slug", "==", normalizedSlug]],
      limit: 2
    });

    return !matches.some((row) => row.id !== excludeStoreId);
  }

  async function generateAvailableStoreSlug(storeName, preferredSlug = "") {
    const base = slugify(preferredSlug || storeName);
    if (!base) throw new Error("A valid store name is required.");

    if (await isStoreSlugAvailable(base)) return base;

    for (let i = 2; i <= 50; i += 1) {
      const candidate = `${base}-${i}`;
      if (await isStoreSlugAvailable(candidate)) return candidate;
    }

    return `${base}-${Date.now().toString(36)}`;
  }

  async function createCreatorStore(input = {}) {
    if (!state.firebaseUser || !state.user) {
      throw new Error("You must be logged in to create a store.");
    }

    if (state.store && input.allowAdditionalStore !== true) {
      throw new Error("A store already exists for this account.");
    }

    const storeName = cleanString(input.storeName || input.store_name);
    if (storeName.length < 2) {
      throw new Error("Store name must contain at least 2 characters.");
    }

    const slug = await generateAvailableStoreSlug(
      storeName,
      input.slug
    );

    const creatorId =
      state.creator?.id ||
      input.creatorId ||
      randomId("creator");

    const storeId = input.storeId || randomId("store");
    const batch = services.db.batch();

    const creatorRef = documentRef(
      CORE_CONFIG.collections.creators,
      creatorId
    );

    const storeRef = documentRef(
      CORE_CONFIG.collections.stores,
      storeId
    );

    const userRef = documentRef(
      CORE_CONFIG.collections.users,
      state.firebaseUser.uid
    );

    if (!state.creator) {
      batch.set(
        creatorRef,
        {
          user_id: state.firebaseUser.uid,
          userId: state.firebaseUser.uid,
          display_name:
            cleanString(input.creatorName) ||
            state.user.name ||
            cleanString(state.firebaseUser.displayName),
          displayName:
            cleanString(input.creatorName) ||
            state.user.name ||
            cleanString(state.firebaseUser.displayName),
          handle: slug,
          category: cleanString(input.category),
          audience: cleanString(input.audience),
          language: cleanString(input.language),
          bio: cleanString(input.bio || input.shortBio),
          short_bio: cleanString(input.shortBio || input.bio),
          shortBio: cleanString(input.shortBio || input.bio),
          support_email: cleanString(input.supportEmail),
          supportEmail: cleanString(input.supportEmail),
          social_links: input.socialLinks || {},
          socialLinks: input.socialLinks || {},
          profile_image: cleanString(input.profileImage),
          profileImage: cleanString(input.profileImage),
          store_id: storeId,
          storeId,
          kyc_status: "not_started",
          payout_status: "not_connected",
          onboarding_status: "in_progress",
          status: "active",
          created_at: serverTimestamp(),
          updated_at: serverTimestamp()
        },
        { merge: true }
      );
    } else {
      batch.update(creatorRef, {
        store_id: storeId,
        storeId,
        updated_at: serverTimestamp()
      });
    }

    const storePayload = {
      owner_id: state.firebaseUser.uid,
      ownerId: state.firebaseUser.uid,
      creator_id: creatorId,
      creatorId,
      store_name: storeName,
      storeName,
      slug,
      tagline: cleanString(input.tagline),
      short_bio: cleanString(input.shortBio || input.short_bio),
      shortBio: cleanString(input.shortBio || input.short_bio),
      about: cleanString(input.about),
      category: cleanString(input.category),
      audience: cleanString(input.audience),
      template: cleanString(input.template || "personal-brand"),
      status: "draft",
      visibility: "private",
      completion_percentage: Number(input.completionPercentage || 20),
      completionPercentage: Number(input.completionPercentage || 20),
      profile_image: cleanString(input.profileImage),
      profileImage: cleanString(input.profileImage),
      banner_image: cleanString(input.bannerImage),
      bannerImage: cleanString(input.bannerImage),
      logo: cleanString(input.logo),
      primary_color: cleanString(input.primaryColor),
      primaryColor: cleanString(input.primaryColor),
      secondary_color: cleanString(input.secondaryColor),
      secondaryColor: cleanString(input.secondaryColor),
      support_email: cleanString(input.supportEmail),
      supportEmail: cleanString(input.supportEmail),
      language: cleanString(input.language),
      onboarding_status: cleanString(input.onboardingStatus || "in_progress"),
      onboardingStatus: cleanString(input.onboardingStatus || "in_progress"),
      selected_product_count: Number(input.selectedProductCount || 0),
      selectedProductCount: Number(input.selectedProductCount || 0),
      social_links: input.socialLinks || {},
      socialLinks: input.socialLinks || {},
      created_at: serverTimestamp(),
      updated_at: serverTimestamp()
    };

    batch.set(storeRef, storePayload, { merge: false });

    batch.update(userRef, {
      creator_id: creatorId,
      creatorId,
      store_id: storeId,
      storeId,
      onboarding_status: "in_progress",
      onboardingStatus: "in_progress",
      updated_at: serverTimestamp()
    });

    await batch.commit();

    state.creator = normalizeCreator(
      {
        ...(state.creator?.raw || {}),
        user_id: state.firebaseUser.uid,
        userId: state.firebaseUser.uid,
        display_name:
          cleanString(input.creatorName) ||
          state.user.name ||
          cleanString(state.firebaseUser.displayName),
        displayName:
          cleanString(input.creatorName) ||
          state.user.name ||
          cleanString(state.firebaseUser.displayName),
        handle: state.creator?.handle || slug,
        category: cleanString(input.category),
        audience: cleanString(input.audience),
        language: cleanString(input.language),
        bio: cleanString(input.bio || input.shortBio),
        supportEmail: cleanString(input.supportEmail),
        socialLinks: input.socialLinks || {},
        store_id: storeId,
        storeId,
        status: "active"
      },
      creatorId
    );

    state.store = normalizeStore(storePayload, storeId);
    state.user = normalizeUser(
      {
        ...state.user.raw,
        uid: state.user.uid,
        creator_id: creatorId,
        store_id: storeId,
        onboarding_status: "in_progress",
        status: state.user.status,
        role: state.user.role
      },
      state.user.uid
    );

    emit("store-created", {
      store: clone(state.store),
      creator: clone(state.creator)
    });

    emit("store-updated", {
      store: clone(state.store)
    });

    const result = {
      store: clone(state.store),
      creator: clone(state.creator),
      user: clone(state.user)
    };

    // Backward compatibility: callers that previously treated the return value
    // as the store can still read store fields directly.
    return {
      ...result.store,
      ...result
    };
  }

  async function updateCurrentStore(data = {}) {
    if (!state.store?.id) {
      throw new Error("No current store is available.");
    }

    const allowed = compactObject({
      store_name: data.storeName ?? data.store_name,
      storeName: data.storeName ?? data.store_name,
      tagline: data.tagline,
      short_bio: data.shortBio ?? data.short_bio,
      shortBio: data.shortBio ?? data.short_bio,
      about: data.about,
      category: data.category,
      audience: data.audience,
      template: data.template,
      status: data.status,
      visibility: data.visibility,
      completion_percentage:
        data.completionPercentage ?? data.completion_percentage,
      completionPercentage:
        data.completionPercentage ?? data.completion_percentage,
      profile_image: data.profileImage ?? data.profile_image,
      profileImage: data.profileImage ?? data.profile_image,
      banner_image: data.bannerImage ?? data.banner_image,
      bannerImage: data.bannerImage ?? data.banner_image,
      logo: data.logo,
      primary_color: data.primaryColor ?? data.primary_color,
      primaryColor: data.primaryColor ?? data.primary_color,
      secondary_color: data.secondaryColor ?? data.secondary_color,
      secondaryColor: data.secondaryColor ?? data.secondary_color,
      support_email: data.supportEmail ?? data.support_email,
      supportEmail: data.supportEmail ?? data.support_email,
      language: data.language,
      onboarding_status: data.onboardingStatus ?? data.onboarding_status,
      onboardingStatus: data.onboardingStatus ?? data.onboarding_status,
      selected_product_count:
        data.selectedProductCount ?? data.selected_product_count,
      selectedProductCount:
        data.selectedProductCount ?? data.selected_product_count,
      social_links: data.socialLinks ?? data.social_links,
      socialLinks: data.socialLinks ?? data.social_links
    });

    if (data.slug && data.slug !== state.store.slug) {
      const slug = slugify(data.slug);

      if (!(await isStoreSlugAvailable(slug, state.store.id))) {
        throw new Error("This store URL is already in use.");
      }

      allowed.slug = slug;
    }

    await updateDocument(
      CORE_CONFIG.collections.stores,
      state.store.id,
      allowed
    );

    const fresh = await getDocument(
      CORE_CONFIG.collections.stores,
      state.store.id
    );

    state.store = normalizeStore(fresh, state.store.id);

    emit("store-updated", {
      store: clone(state.store)
    });

    return clone(state.store);
  }

  // ============================================================
  // CREATOR PRODUCT HELPERS
  // ============================================================

  async function listStoreProducts(storeId = state.store?.id, options = {}) {
    const id = cleanString(storeId);
    if (!id) return [];

    const filters = [["storeId", "==", id]];

    if (options.status) {
      filters.push(["status", "==", normalizeStatus(options.status)]);
    }

    try {
      return await queryDocuments(CORE_CONFIG.collections.products, {
        filters,
        orderBy: options.orderBy || [],
        limit: Number(options.limit || 100)
      });
    } catch (error) {
      // Legacy fallback for records using store_id.
      const legacyFilters = [["store_id", "==", id]];

      if (options.status) {
        legacyFilters.push(["status", "==", normalizeStatus(options.status)]);
      }

      return queryDocuments(CORE_CONFIG.collections.products, {
        filters: legacyFilters,
        limit: Number(options.limit || 100)
      });
    }
  }

  async function createCreatorProduct(data = {}, options = {}) {
    if (!state.firebaseUser) {
      throw new Error("You must be logged in to create a product.");
    }

    const storeId = cleanString(data.storeId || data.store_id || state.store?.id);
    const creatorId = cleanString(
      data.creatorId || data.creator_id || state.creator?.id
    );

    if (!storeId || !creatorId) {
      throw new Error("Creator and store context are required.");
    }

    const title = cleanString(data.title || data.name);
    if (!title) throw new Error("Product name is required.");

    const payload = compactObject({
      ownerId: state.firebaseUser.uid,
      owner_id: state.firebaseUser.uid,
      creatorId,
      creator_id: creatorId,
      storeId,
      store_id: storeId,
      templateId: cleanString(data.templateId || data.template_id),
      template_id: cleanString(data.templateId || data.template_id),
      type: cleanString(data.type || data.fulfilmentType || data.fulfillmentType),
      fulfilmentType: cleanString(
        data.fulfilmentType || data.fulfillmentType || data.type
      ),
      fulfillmentType: cleanString(
        data.fulfillmentType || data.fulfilmentType || data.type
      ),
      title,
      shortDescription: cleanString(
        data.shortDescription || data.short_description
      ),
      short_description: cleanString(
        data.shortDescription || data.short_description
      ),
      description: cleanString(data.description),
      category: cleanString(data.category),
      price: Number(data.price || 0),
      currency: cleanString(
        data.currency || CORE_CONFIG.defaults.currency
      ).toUpperCase(),
      status: normalizeStatus(data.status || "draft"),
      visibility: normalizeStatus(data.visibility || "private"),
      sortOrder: Number(data.sortOrder || data.sort_order || 0),
      sort_order: Number(data.sortOrder || data.sort_order || 0),
      ...data
    });

    return createDocument(CORE_CONFIG.collections.products, payload, {
      id: options.id,
      merge: options.merge
    });
  }

  async function updateCreatorProduct(productId, data = {}) {
    const id = cleanString(productId);
    if (!id) throw new Error("Product ID is required.");

    const payload = compactObject({
      ...data,
      status:
        data.status !== undefined ? normalizeStatus(data.status) : undefined,
      visibility:
        data.visibility !== undefined
          ? normalizeStatus(data.visibility)
          : undefined
    });

    return updateDocument(CORE_CONFIG.collections.products, id, payload);
  }

  // ============================================================
  // ACTIVITY LOGGING AND ERROR HANDLING
  // ============================================================

  async function logActivity(action, details = {}) {
    const payload = {
      action: cleanString(action, "unknown"),
      user_id: state.firebaseUser?.uid || "",
      creator_id: state.creator?.id || "",
      store_id: state.store?.id || "",
      page: state.page?.name || "",
      details,
      user_agent: navigator.userAgent,
      created_at: serverTimestamp()
    };

    try {
      await collectionRef(CORE_CONFIG.collections.activityLogs).add(payload);
      return true;
    } catch (error) {
      safeConsole("warn", "[Clikker] Activity log failed:", error);
      return false;
    }
  }

  function friendlyFirebaseMessage(error) {
    const code = cleanString(error?.code).toLowerCase();

    const map = {
      "auth/email-already-in-use": "This email address is already registered.",
      "auth/invalid-email": "Please enter a valid email address.",
      "auth/user-not-found": "No account was found for this email address.",
      "auth/wrong-password": "The password is incorrect.",
      "auth/invalid-credential": "The email or password is incorrect.",
      "auth/weak-password": "Use a stronger password with at least 6 characters.",
      "auth/too-many-requests": "Too many attempts. Please try again later.",
      "auth/popup-closed-by-user": "Google sign-in was cancelled.",
      "permission-denied": "You do not have permission to perform this action.",
      "storage/unauthorized": "You do not have permission to upload this file.",
      "storage/canceled": "The upload was cancelled.",
      "storage/quota-exceeded": "Storage limit reached. Please contact support.",
      unavailable: "The service is temporarily unavailable. Please try again."
    };

    return map[code] || cleanString(error?.message, "Something went wrong.");
  }

  function handleError(error, context = "unknown", notify = true) {
    const message = friendlyFirebaseMessage(error);

    safeConsole("error", `[Clikker:${context}]`, error);

    emit("error", {
      context,
      code: error?.code || "",
      message
    });

    if (notify) showToast(message, "error");

    return {
      context,
      code: error?.code || "",
      message,
      originalError: error
    };
  }

  // ============================================================
  // PUBLIC API
  // ============================================================

  const Clikker = {
    __coreLoaded: true,
    __firebaseLoaded: true,
    fileName: "clikker-firebase.js",
    version: CORE_CONFIG.version,
    config: CORE_CONFIG,
    firebaseConfig: FIREBASE_CONFIG,
    readyPromise,

    async waitUntilReady() {
      return readyPromise;
    },

    get app() {
      return services.app;
    },

    get auth() {
      return services.auth;
    },

    get db() {
      return services.db;
    },

    get storage() {
      return services.storage;
    },

    get ready() {
      return state.ready;
    },

    get currentFirebaseUser() {
      return state.firebaseUser;
    },

    get currentUser() {
      return clone(state.user);
    },

    get currentCreator() {
      return clone(state.creator);
    },

    get currentStore() {
      return clone(state.store);
    },

    get page() {
      return clone(state.page);
    },

    // Utilities
    emit,
    sleep,
    clone,
    cleanString,
    normalizeEmail,
    compactObject,
    toArray,
    unique,
    escapeHTML,
    safeURL,
    slugify,
    randomId,
    parseDate,
    formatDate,
    formatDateTime,
    formatCurrency,
    formatMoneyFromPaise,
    copyToClipboard,
    isOnline,
    getStateSnapshot,
    redirect,
    addQueryParams,

    // UI
    showToast,
    setLoading,
    confirmAction,

    // Firebase values
    serverTimestamp,
    increment,
    arrayUnion,
    arrayRemove,
    deleteField,
    timestampFromDate,

    // Firestore
    collectionRef,
    documentRef,
    getDocument,
    createDocument,
    setDocument,
    updateDocument,
    deleteDocument,
    queryDocuments,
    listenToDocument,
    listenToQuery,
    runTransaction,
    runBatch,

    // Storage
    validateFile,
    uploadFile,
    deleteFile,

    // Data compatibility
    normalizeUser,
    normalizeCreator,
    normalizeStore,
    normalizeOrder,

    // Auth
    loginWithEmail,
    registerWithEmail,
    loginWithGoogle,
    persistSignupConsent,
    sendPasswordReset,
    logout,
    isAuthenticated,
    isAccountActive,
    hasRole,

    // Creator store
    isStoreSlugAvailable,
    generateAvailableStoreSlug,
    createCreatorStore,
    updateCurrentStore,

    // Creator products
    listStoreProducts,
    createCreatorProduct,
    updateCreatorProduct,

    // Logging and errors
    logActivity,
    handleError,

    async refreshContext() {
      if (!services.auth?.currentUser) {
        state.firebaseUser = null;
        state.user = null;
        state.creator = null;
        state.store = null;

        return {
          firebaseUser: null,
          user: null,
          creator: null,
          store: null
        };
      }

      const context = await loadAuthenticatedContext(
        services.auth.currentUser
      );

      state.authResolved = true;
      state.ready = true;
      applyPageAccessRules();
      emit("context-refreshed", context);
      return context;
    },

    // Alias used by creator workspace templates.
    async refreshCurrentContext() {
      return this.refreshContext();
    },

    requireAuth() {
      if (!state.firebaseUser) {
        redirect(CORE_CONFIG.routes.login);
        return false;
      }

      return true;
    },

    requireRole(roles) {
      if (!this.requireAuth()) return false;

      if (!hasRole(roles)) {
        showToast("You do not have permission to access this page.", "error");
        redirect(CORE_CONFIG.routes.dashboard);
        return false;
      }

      return true;
    },

    destroy() {
      if (typeof state.authUnsubscribe === "function") {
        state.authUnsubscribe();
      }

      state.authUnsubscribe = null;
      state.ready = false;
      emit("destroyed", {});
    }
  };

  window.Clikker = Clikker;

  // Backward compatibility for old dashboard code.
  window.formatINR = window.formatINR || function (num) {
    return new Intl.NumberFormat("en-IN").format(Number(num || 0));
  };

  // ============================================================
  // BOOT
  // ============================================================

  async function boot() {
    if (state.initialized) return;

    try {
      state.page = getPageConfig();

      initializeFirebase();
      startAuthObserver();

      document.documentElement.classList.add("clikker-core-loaded");

      if (!window.__clikkerNetworkListenersBound) {
        window.__clikkerNetworkListenersBound = true;

        window.addEventListener("online", () => {
          emit("network-changed", { online: true });
          showToast("You are back online.", "success", { duration: 2500 });
        });

        window.addEventListener("offline", () => {
          emit("network-changed", { online: false });
          showToast(
            "You are offline. Some actions may be unavailable.",
            "warning",
            { duration: 0 }
          );
        });
      }

      safeConsole(
        "info",
        `[Clikker] Firebase ${CORE_CONFIG.version} initialized for ${FIREBASE_CONFIG.projectId}${state.page?.name === "public-store" ? " (public-store fast path)" : ""}.`
      );
    } catch (error) {
      handleError(error, "boot");
      settleReady(null, error);
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot, { once: true });
  } else {
    boot();
  }
})(window, document);
